import React, { useEffect, useState } from "react";
import { Link, Navigate, Route, Routes, useNavigate, useParams } from "react-router-dom";
import AgentLayout from "./components/layout/AgentLayout";
import PageHeader from "./components/common/PageHeader";
import { DASHBOARD_CRUMB } from "./components/common/Breadcrumb";
import DashboardPage from "./pages/DashboardPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import LoginPage from "./pages/LoginPage";
import BookingDetailPage from "./pages/BookingDetailPage";
import BookingVoucherPage from "./pages/BookingVoucherPage";
import BookingsPage from "./pages/BookingsPage";
import CustomersPage from "./pages/CustomersPage";
import CustomerFormPage from "./pages/CustomerFormPage";
import PackageBookingPage from "./pages/PackageBookingPage";
import PackageDetailPage from "./pages/PackageDetailPage";
import PackagesPage from "./pages/PackagesPage";
import ReportsPage from "./pages/ReportsPage";
import RegisterPage from "./pages/RegisterPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import VerifyEmailPage from "./pages/VerifyEmailPage";
import WaitingApprovalPage from "./pages/WaitingApprovalPage";
import OnboardingPage from "./pages/OnboardingPage";
import ProfilePage from "./pages/ProfilePage";
import AgencyDetailPage from "./pages/AgencyDetailPage";
import WalletPage from "./pages/WalletPage";
import UsersPage from "./pages/UsersPage";
import PaymentProofPage from "./pages/PaymentProofPage";
import DocumentsPage from "./pages/DocumentsPage";
import PassengersPage from "./pages/PassengersPage";
import NotificationsPage from "./pages/NotificationsPage";
import SupportTicketsPage from "./pages/SupportTicketsPage";
import SupportTicketDetailPage from "./pages/SupportTicketDetailPage";
import VisasPage from "./pages/VisasPage";
import VisaDetailPage from "./pages/VisaDetailPage";
import HotelsPage from "./pages/HotelsPage";
import HotelDetailPage from "./pages/HotelDetailPage";
import TransportationPage from "./pages/TransportationPage";
import TransportationDetailPage from "./pages/TransportationDetailPage";
import FlightsPage from "./pages/FlightsPage";
import FlightDetailPage from "./pages/FlightDetailPage";
import StaticHtmlPage from "./pages/StaticHtmlPage";
import { RedirectIfAuthenticated, RequireAgentAuth } from "./components/auth/AgentRouteGuards";
import { NavigationHistoryProvider } from "./components/common/NavigationHistory";
import { fetchCurrentAgent } from "./lib/agentAuth";
import { canWriteBookings, roleFromProfile } from "./lib/agencyAccess";

function ZiyaratListPage() {
  return (
    <AgentLayout>
      <div className="container-fluid py-24">
        <PageHeader
          eyebrow="Ziyarat"
          title="Ziyarat Packages"
          description="Read-only ziyarat packages with route, duration, and booking actions."
          items={[DASHBOARD_CRUMB, { label: "Services" }, { label: "Ziyarat" }]}
        />
        <StaticHtmlPage fileName="ziyarath-packages.html" />
      </div>
    </AgentLayout>
  );
}

function ZiyaratDetailPage() {
  const { id } = useParams();
  const [canBook, setCanBook] = useState(false);

  useEffect(() => {
    fetchCurrentAgent()
      .then((profile) => setCanBook(canWriteBookings(roleFromProfile(profile))))
      .catch(() => setCanBook(false));
  }, []);

  return (
    <AgentLayout>
      <div className="container-fluid py-24">
        <PageHeader
          eyebrow="Ziyarat"
          title="Ziyarat detail"
          description="Read-only ziyarat package with route, sites, and guide summary."
          items={[
            DASHBOARD_CRUMB,
            { label: "Services" },
            { label: "Ziyarat", href: "/ziyarat-packages" },
            { label: "Ziyarat detail" },
          ]}
          backTo="/ziyarat-packages"
          actions={
            canBook ? (
              <Link to={`/ziyarat-packages/${id}/book`} className="btn btn-primary btn-sm">Book Ziyarath</Link>
            ) : null
          }
        />
        <StaticHtmlPage fileName="ziyarath-package-detail.html" />
      </div>
    </AgentLayout>
  );
}

function ZiyaratBookingPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [allowed, setAllowed] = useState(false);

  useEffect(() => {
    let alive = true;
    fetchCurrentAgent()
      .then((profile) => {
        if (!alive) return;
        if (!canWriteBookings(roleFromProfile(profile))) {
          navigate(`/ziyarat-packages/${id}`, { replace: true });
          return;
        }
        setAllowed(true);
      })
      .catch(() => {
        if (alive) navigate(`/ziyarat-packages/${id}`, { replace: true });
      });
    return () => {
      alive = false;
    };
  }, [id, navigate]);

  if (!allowed) {
    return null;
  }

  return (
    <AgentLayout>
      <div className="container-fluid py-24">
        <PageHeader
          eyebrow="Ziyarat"
          title="Ziyarat booking"
          description="Selected ziyarat package carried forward from the list or detail page."
          items={[
            DASHBOARD_CRUMB,
            { label: "Services" },
            { label: "Ziyarat", href: "/ziyarat-packages" },
            { label: "Ziyarat detail", href: `/ziyarat-packages/${id}` },
            { label: "Ziyarat booking" },
          ]}
          backTo={`/ziyarat-packages/${id}`}
        />
        <StaticHtmlPage fileName="ziyarath-package-booking.html" />
      </div>
    </AgentLayout>
  );
}

export default function App() {
  return (
    <NavigationHistoryProvider>
      <Routes>
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        <Route path="/login" element={<RedirectIfAuthenticated><LoginPage /></RedirectIfAuthenticated>} />
        <Route path="/register" element={<RedirectIfAuthenticated><RegisterPage /></RedirectIfAuthenticated>} />
        <Route path="/forgot-password" element={<RedirectIfAuthenticated><ForgotPasswordPage /></RedirectIfAuthenticated>} />
        <Route path="/reset-password" element={<RedirectIfAuthenticated><ResetPasswordPage /></RedirectIfAuthenticated>} />
        <Route path="/verify-email" element={<RedirectIfAuthenticated><VerifyEmailPage /></RedirectIfAuthenticated>} />

        <Route element={<RequireAgentAuth />}>
          <Route path="/waiting-approval" element={<WaitingApprovalPage />} />
          <Route path="/onboarding" element={<OnboardingPage />} />
          <Route path="/dashboard" element={<DashboardPage />} />

          <Route path="/agency-detail" element={<AgencyDetailPage />} />
          <Route path="/wallet-credit" element={<WalletPage />} />
          <Route path="/documents" element={<DocumentsPage />} />
          <Route path="/reports" element={<ReportsPage />} />
          <Route path="/notifications" element={<NotificationsPage />} />
          <Route path="/support" element={<SupportTicketsPage />} />
          <Route path="/support/:ticketId" element={<SupportTicketDetailPage />} />
          <Route path="/customers" element={<CustomersPage />} />
          <Route path="/customers/new" element={<CustomerFormPage />} />
          <Route path="/customers/:customerId/edit" element={<CustomerFormPage />} />
          <Route path="/users" element={<UsersPage />} />
          <Route path="/settings" element={<ProfilePage />} />
          <Route path="/payment-proof" element={<PaymentProofPage />} />
          <Route path="/passengers" element={<PassengersPage />} />

          <Route path="/bookings" element={<BookingsPage />} />
          <Route path="/bookings/:id" element={<BookingDetailPage />} />
          <Route path="/bookings/:id/voucher" element={<BookingVoucherPage />} />

          <Route path="/packages" element={<PackagesPage />} />
          <Route path="/packages/:id" element={<PackageDetailPage />} />
          <Route path="/packages/:id/book" element={<PackageBookingPage />} />

          <Route path="/hotels" element={<HotelsPage />} />
          <Route path="/hotels/:id" element={<HotelDetailPage />} />
          <Route path="/hotels/:id/book" element={<HotelDetailPage />} />

          <Route path="/transportation" element={<TransportationPage />} />
          <Route path="/transportation/:id" element={<TransportationDetailPage />} />
          <Route path="/transportation/:id/book" element={<TransportationDetailPage />} />

          <Route path="/visas" element={<VisasPage />} />
          <Route path="/visas/:id" element={<VisaDetailPage />} />
          <Route path="/visas/:id/book" element={<VisaDetailPage />} />

          <Route path="/flights" element={<FlightsPage />} />
          <Route path="/flights/:id" element={<FlightDetailPage />} />
          <Route path="/flights/:id/book" element={<FlightDetailPage />} />

          <Route path="/ziyarat-packages" element={<ZiyaratListPage />} />
          <Route path="/ziyarat-packages/:id" element={<ZiyaratDetailPage />} />
          <Route path="/ziyarat-packages/:id/book" element={<ZiyaratBookingPage />} />
        </Route>

        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </NavigationHistoryProvider>
  );
}
